import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Bold,
  Italic,
  Strikethrough,
  Code,
  Smile,
  Plus,
  Info,
} from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import { CHAR_LIMITS, TC_RADIUS } from "../../constants/templateCreatorConstants";
import {
  validateBody,
  detectVariablesInText,
} from "../../utils/templateCreatorValidation";
import FormFieldError from "./FormFieldError";

const BodyEditor = ({
  body,
  variableType,
  bodyVariables,
  onBodyChange,
  onVariablesChange,
}) => {
  const textareaRef = useRef(null);
  const emojiRef = useRef(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [emojiPickerStyle, setEmojiPickerStyle] = useState({
    top: 100,
    left: 100,
  });

  const error = validateBody(body, variableType);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (emojiRef.current?.contains(e.target)) return;
      if (e.target.closest("[data-emoji-picker-portal]")) return;
      setShowEmoji(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (showEmoji && emojiRef.current) {
      const rect = emojiRef.current.getBoundingClientRect();
      const top = Math.min(rect.top - 358 - 8, window.innerHeight - 362);
      const left = Math.min(rect.left, window.innerWidth - 308);
      setEmojiPickerStyle({ top: Math.max(8, top), left: Math.max(8, left) });
    }
  }, [showEmoji]);

  useEffect(() => {
    const detected = detectVariablesInText(body, variableType);
    const existingMap = {};
    bodyVariables.forEach((v) => {
      existingMap[String(v.name)] = v.sample;
    });
    const newVars = detected.map((name) => ({
      name: String(name),
      sample: existingMap[String(name)] || "",
    }));
    const changed =
      newVars.length !== bodyVariables.length ||
      newVars.some(
        (v, i) =>
          v.name !== bodyVariables[i]?.name ||
          v.sample !== bodyVariables[i]?.sample
      );
    if (changed) onVariablesChange(newVars);
  }, [body, variableType]);

  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const before = body.substring(0, start);
    const after = body.substring(end);
    onBodyChange(before + text + after);
    setTimeout(() => {
      ta.focus();
      const pos = start + text.length;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const applyFormat = (wrapper) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = body.substring(start, end);
    if (!selected) return;
    const formatted = `${wrapper}${selected}${wrapper}`;
    onBodyChange(body.substring(0, start) + formatted + body.substring(end));
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(
        start + wrapper.length,
        start + wrapper.length + selected.length
      );
    }, 0);
  };

  const handleAddVariable = () => {
    if (variableType === "named") {
      const existing = detectVariablesInText(body, "named");
      insertAtCursor(`{{param_${existing.length + 1}}}`);
    } else {
      const existing = detectVariablesInText(body, "numbered");
      const next = existing.length > 0 ? Math.max(...existing) + 1 : 1;
      insertAtCursor(`{{${next}}}`);
    }
  };

  const handleEmojiClick = (emojiData) => {
    insertAtCursor(emojiData.emoji);
    setShowEmoji(false);
  };

  const handleSampleChange = (varName, newSample) => {
    onVariablesChange(
      bodyVariables.map((v) =>
        v.name === varName ? { ...v, sample: newSample } : v
      )
    );
  };

  const charOverLimit = body.length > CHAR_LIMITS.BODY;

  return (
    <div>
      <label className="text-[13px] font-semibold text-[#050505] mb-1.5 block">
        Body
      </label>

      <div
        className={`relative border overflow-visible bg-white transition-colors ${TC_RADIUS} ${
          error ? "border-red-300" : "border-[#ccd0d5] focus-within:border-[#1877f2]"
        }`}
      >
        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => onBodyChange(e.target.value)}
          placeholder=""
          rows={7}
          className="w-full px-3 pt-2.5 pb-8 text-[15px] resize-none focus:outline-none leading-relaxed text-[#050505]"
        />

        <span
          className={`absolute bottom-[42px] right-3 text-[12px] tabular-nums pointer-events-none ${
            charOverLimit ? "text-red-600 font-semibold" : "text-[#65676b]"
          }`}
        >
          {body.length}/{CHAR_LIMITS.BODY}
        </span>

        <div className="flex items-center gap-0.5 px-2 py-1.5 border-t border-[#dddfe2] bg-[#f0f2f5]">
          <div ref={emojiRef}>
            <button
              type="button"
              onClick={() => setShowEmoji(!showEmoji)}
              className="p-1.5 text-[#65676b] hover:text-[#050505] hover:bg-[#e4e6eb] rounded transition-colors"
              title="Emoji"
            >
              <Smile size={18} />
            </button>
          </div>

          {[
            { icon: Bold, format: "*", title: "Bold" },
            { icon: Italic, format: "_", title: "Italic" },
            { icon: Strikethrough, format: "~", title: "Strikethrough" },
            { icon: Code, format: "```", title: "Monospace" },
          ].map(({ icon: Icon, format, title }) => (
            <button
              key={title}
              type="button"
              onClick={() => applyFormat(format)}
              className="p-1.5 text-[#65676b] hover:text-[#050505] hover:bg-[#e4e6eb] rounded transition-colors"
              title={title}
            >
              <Icon size={16} />
            </button>
          ))}

          <div className="flex-1" />

          <button
            type="button"
            onClick={handleAddVariable}
            className="flex items-center gap-0.5 px-2 py-1 text-[13px] font-semibold text-[#1877f2] hover:bg-[#e7f3ff] rounded transition-colors"
          >
            <Plus size={14} strokeWidth={2.5} />
            Add variable
          </button>

          <button type="button" className="p-1.5 text-[#65676b]" title="Info">
            <Info size={16} />
          </button>
        </div>
      </div>

      {showEmoji &&
        createPortal(
          <div
            data-emoji-picker-portal
            className={`fixed z-[9999] shadow-xl overflow-hidden bg-white border border-[#dddfe2] ${TC_RADIUS}`}
            style={emojiPickerStyle}
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              width={300}
              height={350}
            />
          </div>,
          document.body
        )}

      <FormFieldError message={error} reserve={false} />

      {bodyVariables.length > 0 && (
        <div className={`mt-3 p-3 bg-[#f0f2f5] border border-[#dddfe2] ${TC_RADIUS}`}>
          <div className="flex items-center justify-between mb-1.5">
            <h4 className="text-[13px] font-semibold text-[#050505]">
              Variable samples
            </h4>
          </div>
          <p className="text-[12px] text-[#65676b] mb-2.5 leading-relaxed">
            Include samples of all variables in your message to help Meta review
            your template. Remember not to include any customer information to
            protect your customer's privacy.
          </p>

          {variableType === "numbered" && (
            <p className="text-[12px] font-semibold text-[#050505] mb-1.5">
              Body
            </p>
          )}

          <div className="space-y-1.5">
            {bodyVariables.map((v) => (
              <div key={v.name} className="flex items-center gap-2">
                <span className={`text-[12px] font-mono text-[#65676b] bg-white px-2 py-1 border border-[#ccd0d5] min-w-[60px] text-center shrink-0 ${TC_RADIUS}`}>
                  {`{{${v.name}}}`}
                </span>
                <input
                  type="text"
                  value={v.sample}
                  onChange={(e) => handleSampleChange(v.name, e.target.value)}
                  placeholder={
                    variableType === "numbered"
                      ? `Enter content for {{${v.name}}}`
                      : `Add sample value of variable`
                  }
                  className={`flex-1 px-2.5 py-1.5 text-[13px] border border-[#ccd0d5] focus:outline-none focus:border-[#1877f2] bg-white ${TC_RADIUS}`}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BodyEditor;
